import { useState } from "react";
import { toast } from "react-toastify";
import { initialClientState } from "./CreateNewForm/Clients/ClientData";
import { initialVendorState } from "./CreateNewForm/Vendor/VendorData";
import { sendEntityFormDataAction } from "../../actions/createEntityFormDataActions";
import { convertToBoolean } from "../../utils/dataConversionUtils";
import { URL_CONFIG } from "../../config/urlConfig";

export const useFormsData = () => {
    const [activeTab, setActiveTab] = useState("client");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [clientFormData, setClientFormData] = useState(initialClientState);
    const [vendorFormData, setVendorFormData] = useState(initialVendorState);

    const [clientErrors, setClientErrors] = useState({});
    const [vendorErrors, setVendorErrors] = useState({});

    // Single field update — functional updater avoids stale closure
    const handleClientChange = (name, value) => {
        setClientFormData(prev => {
            const updated = { ...prev, [name]: value };
            // When LOB type changes to non-travel, default nationality to Domestic
            if (name === 'entityLOBType' && value !== 'travel') {
                updated.selectedlobtypenationality = 'Domestic';
            }
            return updated;
        });
        if (clientErrors[name]) {
            setClientErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
    };

    // Batch update — used for pincode auto-fill (city + State in one render)
    const handleClientChangeMultiple = (fields) => {
        setClientFormData(prev => ({ ...prev, ...fields }));
    };

    const handleClientBlur = () => { };

    const handleVendorChange = (name, value) => {
        setVendorFormData(prev => ({ ...prev, [name]: value }));
        if (vendorErrors[name]) {
            setVendorErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
    };

    const handleSubmit = async (data, type) => {
        setIsSubmitting(true);
        // Build the document upload redirect URL
        const baseUrl = URL_CONFIG.BASE_URL;
        const documentUploadURL = `${baseUrl}/upload-documents?`;
        // Build payload exactly matching server schema
        const now = new Date().toISOString();
        const payload = {


            // Entity category & LOB
            entityCategory: data.entityCategory || "",
            lobType: data.entityLOBType || "",
            lobNationality: data.entityLOBType === 'travel' ? (data.selectedlobtypenationality || "") : "Domestic",

            // Entity information
            entityId: "",
            entityName: data.entityName || "",
            entityType: data.entityType || type,
            entityAddress: data.address || "",
            pinCode: parseInt(data.pinCode, 10) || 0,
            city: data.city || "",
            state: data.State || "",
            aaDepartmentFunctionId: parseInt(data.aaDepartmentFunction, 10) || 0,
            natureOfBusiness: data.natureOfBusiness || "",
            panNumber: data.panNumber || "",
            companyPAN: data.CompanypanNumber || "",

            // GST
            isGSTRegistered: convertToBoolean(data.isGSTRegistered),
            gstNumber: data.gstNumberPrimary || "",
            gstAddress: "",
            otherGSTNumbers: data.gstNumbersOther || "",

            // MSME
            isRegisteredUnderMSME: convertToBoolean(data.isMSMERegistered),

            // Contact person
            contactPerson: data.contactPersonName || "",
            designation: data.contactPersonDesignation || "",
            mobileNumber: data.contactPersonMobile || "",
            emailAddress: data.contactPersonEmail || "",

            // Third party
            thirdPartyPayment: false,
            relationWithBeneficiary: "",

            // Bank details
            bankName: data.bankName || "",
            accountNumber: data.accountNumber || "",
            ifscCode: data.ifscCode || "",
            branchNameAndAddress: data.branchName || "",
            beneficiaryName: data.beneficiaryName || "",

            // Email notification
            sendMeEmail: convertToBoolean(data.sendEmailReceipt),

            // Document upload redirect URL
            documentUploadURL,

            createdOn: now,
            modifiedOn: now,
        };
        try {
            const result = await sendEntityFormDataAction(payload);
            if (result?.success) {
                toast.success(
                    `${type === "client" ? "Client" : "Vendor"} created successfully!`,
                    { position: "top-right", autoClose: 5000 }
                );
                // Reset form
                if (type === "client") {
                    setClientFormData(initialClientState);
                    setClientErrors({});
                } else {
                    setVendorFormData(initialVendorState);
                    setVendorErrors({});
                }
            } else {
                toast.error(result?.error || "Failed to create. Please try again.", {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(error.message || "An error occurred while submitting the form", {
                position: "top-right",
                autoClose: 5000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (activeTab === "client") {
            setClientFormData(initialClientState);
            setClientErrors({});
        } else {
            setVendorFormData(initialVendorState);
            setVendorErrors({});
        }
        toast.info("Form cleared", { position: "top-right", autoClose: 5000 });
    };

    const handleTabChange = (tab) => {
        if (!isSubmitting) setActiveTab(tab);
    };

    return {
        activeTab,
        isSubmitting,
        clientFormData,
        vendorFormData,
        clientErrors,
        vendorErrors,
        setClientErrors,
        setVendorErrors,
        handleClientChange,
        handleClientChangeMultiple,
        handleClientBlur,
        handleVendorChange,
        handleSubmit,
        handleCancel,
        handleTabChange,
    };
};
